import {
  AlertOutlined,
  ApiOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DislikeOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  HeartOutlined,
  InfoCircleOutlined,
  LikeOutlined,
  MessageOutlined,
  PhoneOutlined,
  SnippetsOutlined,
} from '@ant-design/icons'
import {
  Button,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Dropdown,
  List,
  Menu,
  message,
  Modal,
  Row,
  Tag,
  Tooltip,
  Typography,

} from 'antd'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import { useNavigate, useParams, Link } from 'react-router-dom'
import queryString from 'query-string'
import dayjs from 'dayjs'
import formatPhoneNumberSafe from '../../lib/phone'
import api from '../../api'
import { useAsync } from '../../hooks'
import { formatDOB } from '../../lib/datetime'
import { useIsAdmin } from '../../redux/slices/session'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import Loading from '../Loading'
import Empty from '../Common/Empty'
import { formatMedicalProvider } from '../Forms/MedicalProviderSelect'
import CommentsTables from '../Comments/CommentsTable'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import FullRemovalModal from './FullRemovalModal'
import TransmissionsModal from './TransmissionsModal'
import WelcomeLetter from './WelcomeLetter'
import TopContent from './TopContent'
import LeadInfo from './LeadInfo'
import EstimatedLongevity from './EstimatedLongevity'
import DiseasesList from './DiseasesList'
import Timeline from './Timeline'

const { Text } = Typography

const useStyles = createUseStyles({
  actions: {
    marginTop: 30,
    textAlign: 'center',
  },
  ok: {
    color: 'green',
  },
  notOk: {
    color: 'red',
  },
  tag: {
    verticalAlign: 'middle',
    marginBottom: 5,
  },
  dangerZoneButton: {
    marginLeft: 'auto',
  },
  modalButtons: {
    textAlign: 'right',
  },
  comments: {
    marginTop: 30,
  },
  miniBlock: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    marginRight: '.5em',
    borderRadius: '4px',
    backgroundColor: '#1890ff',
    color: '#1890ff',
  },
  trDate: {
    display: 'inline-block',
    minWidth: '13rem',
    width: '10%',
    padding: '0 2rem',
  },
  trDevice: {
    display: 'inline-block',
    margin: '0 2rem',
    minWidth: '10rem',
  },
})

export default function Details() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isAdmin = useIsAdmin()
  const [practice, setPractice] = useState({
    id: '',
  })
  const [medicalProviders, setMedicalProviders] = useState(null)
  const [officeLocation, setOfficeLocation] = useState(null)

  const goToEdit = useCallback(
    () => navigate(toPath.patients.patient.edit({ id })),
    [navigate, id],
  )
  const goToAlert = () =>
    navigate(
      toPath.alerts.new().concat('?', queryString.stringify({ patientId: id, from: 'patient' })),
    )
  const goToDevices = () => navigate(toPath.patients.patient.devices({ id }))

  const [removalOptionsVisible, setRemovalOptionsVisible] = useState(false)
  const hideRemovalOptions = useCallback(
    () => setRemovalOptionsVisible(false),
    [],
  )
  const showRemovalOptions = useCallback(
    () => setRemovalOptionsVisible(true),
    [],
  )

  const [remindersOptionsVisible, setRemindersOptionsVisible] = useState(false)
  const hideRemindersOptions = useCallback(
    () => setRemindersOptionsVisible(false),
    [],
  )
  const showRemindersOptions = useCallback(
    () => setRemindersOptionsVisible(true),
    [],
  )

  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false)
  const hidePreferencesModal = useCallback(
    () => setPreferencesModalVisible(false),
    [],
  )
  const showPreferencesModal = useCallback(
    () => setPreferencesModalVisible(true),
    [],
  )
  const [showTransmissionsModal, setShowTransmissionsModal] = useState(false)
  const showTransmissions = useCallback(
    () => setShowTransmissionsModal(true),
    [],
  )

  const [fullRemovalVisible, setFullRemovalVisible] = useState(false)
  const [remindTransmissions, setRemindTransmissions] = useState(false)
  const [notifyDataReception, setNotifyDataReception] = useState(false)
  const [transmissionsCount, setTransmissionsCount] = useState(0)
  const [transmissionsList, setTransmissionsList] = useState(0)
  const hideFullRemoval = useCallback(() => setFullRemovalVisible(false), [])
  const showFullRemoval = useCallback(() => setFullRemovalVisible(true), [])
  const getPatient = useCallback(async () => {
    const patient = await api.patients.get(id)
    setRemindTransmissions(patient.preferences.remindTransmissions)
    setNotifyDataReception(patient.preferences.notifyDataReception)

    return patient
  }, [id])
  const {
    pending,
    value: patient,
    error,
    clearError,
    execute,
  } = useAsync(getPatient)

  // Load practice to get the logo.
  useEffect(() => {
    ;(async () => {
      if (patient?.practice?.id) {
        setPractice(await api.practices.getPractice(patient.practice.id))
        if (!medicalProviders) {
          setMedicalProviders(
            await api.practices.getMedicalProviders(patient.practice.id),
          )
        }
      }
    })()
  }, [medicalProviders, patient])

  const medicalProvider = useMemo(() => {
    const provider = medicalProviders?.find(
      p => p.id === patient?.medicalProvider?.id,
    )

    return provider ? formatMedicalProvider(provider) : ''
  }, [medicalProviders, patient])

  // eslint-disable-next-line consistent-return
  useMemo(() => {
    if (!patient?.officeLocation) return null

    setOfficeLocation(patient.officeLocation)
  }, [patient])

  const deactivate = useCallback(async () => {
    try {
      await api.patients.destroy(id)
      hideRemovalOptions()
      message.success('Patient successfully deactivated')
      execute()
    } catch (e) {
      message.error(`Could not deactivate patient: ${e.message}`)
    }
  }, [execute, hideRemovalOptions, id])
  const reactivate = useCallback(async () => {
    try {
      await api.patients.update(id, { active: true })
      message.success('Patient successfully reactivated')
      execute()
    } catch (e) {
      message.error(`Could not reactivate patient: ${e.message}`)
    }
  }, [execute, id])
  const approve = useCallback(async () => {
    try {
      await api.patients.update(id, { approved: true })
      message.success('Patient successfully approved')
      execute()
    } catch (e) {
      message.error(`Could not approve patient: ${e.message}`)
    }
  }, [execute, id])
  const unapprove = useCallback(async () => {
    try {
      await api.patients.update(id, { approved: false })
      message.success('Patient successfully unapproved')
      execute()
    } catch (e) {
      message.error(`Could not unapprove patient: ${e.message}`)
    }
  }, [execute, id])
  const remindCall = useCallback(async () => {
    try {
      await api.patients.remind([id], 'voice')
      hideRemindersOptions()
      message.success('Reminder call sent')
      execute()
    } catch (e) {
      message.error(`Could not remind patient: ${e.message}`)
    }
  }, [execute, hideRemindersOptions, id])
  const remindSms = useCallback(async () => {
    try {
      await api.patients.remind([id], 'sms')
      hideRemindersOptions()
      message.success('Reminder SMS sent')
      execute()
    } catch (e) {
      message.error(`Could not remind patient: ${e.message}`)
    }
  }, [execute, hideRemindersOptions, id])

  const cancelPreferences = useCallback(() => {
    setRemindTransmissions(patient.preferences.remindTransmissions)
    setNotifyDataReception(patient.preferences.notifyDataReception)
    setPreferencesModalVisible(false)
  }, [patient])

  const savePreferences = useCallback(async () => {
    try {
      await api.patients.savePreferences(id, {
        remindTransmissions,
        notifyDataReception,
      })
      await getPatient()
      hidePreferencesModal()
      message.success('Preferences saved successfully')
    } catch (e) {
      message.error(`Could not save preferences: ${e.message}`)
    }
  }, [
    hidePreferencesModal,
    id,
    remindTransmissions,
    notifyDataReception,
    getPatient,
  ])

  const goToOfficeCheck = () => {
    navigate(
      toPath.practices.officeChecks
        .new({
          practiceId: practice?.id || patient?.practiceId,
        })
        .concat('?', queryString.stringify({ patientId: id })),
    )
  }

  const handleDataReceptionChange = e => {
    setNotifyDataReception(e.target.checked)
  }

  const handleReminderChange = e => {
    setRemindTransmissions(e.target.checked)
  }

  const { pending: deactivating, execute: handleDeactivate } = useAsync(
    deactivate,
    false,
  )
  const { pending: makingReminderCall, execute: makeReminderCall } = useAsync(
    remindCall,
    false,
  )
  const { pending: sendingReminderSms, execute: sendReminderSms } = useAsync(
    remindSms,
    false,
  )
  const classes = useStyles()

  const getTransmissions = useCallback(
    async () => api.patients.getTransmissions(id, 5),
    [id],
  )

  const { value: transmissionsResponse } = useAsync(getTransmissions)

  useEffect(() => {
    setTransmissionsList(transmissionsResponse?.body || [])
    setTransmissionsCount(
      transmissionsResponse?.headers?.get('X-Total') || 'N/A',
    )
  }, [transmissionsResponse])

  if (pending) return <Loading large />

  if (error) {
    return <ListError description={error.message} onClose={clearError} />
  }
  const { currentDevice: device } = patient

  const showWelcomeLetterButton =
    Number.isInteger(patient?.letterStatus) && patient?.welcomeLetterTemplate

  return (
    <div>
      <PageHeader
        title={
          <>
            {`${patient.firstName} ${patient.lastName}`}{' '}
            {!patient.active && <Tag className={classes.tag}>deactivated</Tag>}
            {patient.approved && (
              <Tag className={classes.tag} color="green">
                approved
              </Tag>
            )}
            {!patient.approved && (
              <Tag className={classes.tag} color="yellow">
                pending approval
              </Tag>
            )}
          </>
        }
        subTitle={isAdmin && patient.practice && patient.practice.name}
        onBack={() => navigate(-1)}
        extra={
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="1" onClick={goToEdit}>
                  <EditOutlined /> Edit
                </Menu.Item>
                <Menu.Item key="2" onClick={goToDevices}>
                  <HeartOutlined /> Manage patient&#39;s devices
                </Menu.Item>
                {isAdmin && !patient.approved && (
                  <Menu.Item key="3" onClick={approve}>
                    <LikeOutlined /> Approve
                  </Menu.Item>
                )}
                {isAdmin && patient.approved && (
                  <Menu.Item key="4" onClick={unapprove}>
                    <DislikeOutlined /> Unapprove
                  </Menu.Item>
                )}
                {isAdmin && !patient.active && (
                  <Menu.Item key="5" onClick={reactivate}>
                    <ApiOutlined /> Reactivate
                  </Menu.Item>
                )}
                {isAdmin && (
                  <Menu.Item key="6" onClick={showRemovalOptions}>
                    <DeleteOutlined /> Removal options
                  </Menu.Item>
                )}
                {isAdmin && (
                  <Menu.Item
                    key="7"
                    onClick={showRemindersOptions}
                    disabled={!remindTransmissions}
                  >
                    <PhoneOutlined /> Send reminder
                  </Menu.Item>
                )}
                {isAdmin && patient.active && (
                  <Menu.Item key="8" onClick={goToAlert}>
                    <AlertOutlined /> Create Alert
                  </Menu.Item>
                )}
                {isAdmin && patient.active && (
                  <Menu.Item key="9" onClick={goToOfficeCheck}>
                    <SnippetsOutlined /> Create Office Check
                  </Menu.Item>
                )}
              </Menu>
            }
          >
            <Button>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
        }
      />
      <TopContent remoteMonitoring={patient.remoteMonitoring} />
      <WelcomeLetter
        patient={{
          ...patient,
          welcome_letter_template: 'manual_active',
        }}
        practice={practice}
        showWelcomeLetterButton={showWelcomeLetterButton}
      />
      <Divider orientation="left">Personal Information</Divider>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="First Name">
          {patient.firstName}
        </Descriptions.Item>
        <Descriptions.Item label="Last Name">
          {patient.lastName}
        </Descriptions.Item>
        <Descriptions.Item label="Date of birth">
          {formatDOB(patient.birthDate)}
        </Descriptions.Item>
        <Descriptions.Item label="E-mail">{patient.email}</Descriptions.Item>
        <Descriptions.Item label="Mobile phone">
          {formatPhoneNumberSafe(patient.mobilePhone)}
        </Descriptions.Item>
        <Descriptions.Item label="Home phone">
          {formatPhoneNumberSafe(patient.homePhone)}
        </Descriptions.Item>
        <Descriptions.Item label="MRN">{patient.practiceMrn}</Descriptions.Item>
      </Descriptions>
      <Divider orientation="left">Address</Divider>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Street">
          {patient.addressStreet}
        </Descriptions.Item>
        <Descriptions.Item label="Apt/Suite">
          {patient.addressApt}
        </Descriptions.Item>
        <Descriptions.Item label="City">
          {patient.addressCity}
        </Descriptions.Item>
        <Descriptions.Item label="State">
          {patient.addressState}
        </Descriptions.Item>
        <Descriptions.Item label="Country">
          {patient.addressCountry}
        </Descriptions.Item>
        <Descriptions.Item label="Zip code">
          {patient.addressZipCode}
        </Descriptions.Item>
      </Descriptions>
      <Divider orientation="left">Insurance Information</Divider>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Insurance Provider">
          {patient.insuranceProvider ? patient.insuranceProvider.name : ''}
        </Descriptions.Item>
        <Descriptions.Item label="Insurance Policy #">
          {patient.insurancePolicyNumber}
        </Descriptions.Item>
      </Descriptions>
      <Divider orientation="left">Medical Provider</Divider>
      <Row>
        <Col span={12}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Provider">
              {medicalProvider}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Office Location">
              {officeLocation?.name}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={12}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="PCP name">
              {patient.pcpName}
            </Descriptions.Item>
            <Descriptions.Item label="PCP phone number">
              {patient.pcpPhoneNumber}
            </Descriptions.Item>
            <Descriptions.Item label="PCP fax number">
              {patient.pcpFaxNumber}
            </Descriptions.Item>
            <div className="alerts-recipients">
              <div className="ant-descriptions-item-label ant-descriptions-item-colon">
                Alerts Recipients
              </div>
              <Descriptions.Item
                label="Alerts Recipients"
                styles={{ label: { verticalAlign: 'top', position: 'relative' } }}
              >
                <List
                  size="small"
                  dataSource={patient.alertRecipients}
                  renderItem={recipient => (
                    <List.Item>{`${recipient.firstName} ${recipient.lastName} (${recipient.email})`}</List.Item>
                  )}
                />
              </Descriptions.Item>
            </div>
          </Descriptions>
        </Col>
      </Row>

      <Divider orientation="left">Cardiac Device</Divider>
      <Row>
        <Col span={10}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Model">
              {device?.modelName || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Company">
              {device?.modelManufacturer || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              {device?.modelType || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Serial Number">
              {device?.serialNumber || 'N/A'}
            </Descriptions.Item>
            {patient.remoteMonitoring && (
              <>
                <Descriptions.Item label="Enrolled">
                  {patient.enrolled ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Paired">
                  {patient.paired ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Monitor Model Number">
                  {patient.homeMonitorSerialNumber}
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        </Col>

        <Col span={6}>
          <EstimatedLongevity
            estimatedBatteryLongevity={
              patient?.currentDevice?.estimatedBatteryLongevity
            }
          />
        </Col>
        <Col span={8}>
          <LeadInfo currentLead={patient.currentLead} />
        </Col>
      </Row>
      <Divider orientation="left">Diseases and History</Divider>
      <DiseasesList patientId={id} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.35em',
          marginTop: 10,
          marginBottom: 10,
        }}
      >
        <CircleCheckIcon
          checked={patient?.alertForAnyAfib}
          className={classes[patient?.alertForAnyAfib ? 'ok' : 'notOk']}
        />
        <span style={{ fontWeight: 600 }}>Alert for Any AFib</span>
        <Tooltip title="Send alerts whenever any atrial fibrillation event is detected for this patient.">
          <InfoCircleOutlined className={classes.alertInfo} />
        </Tooltip>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.35em',
          marginTop: 10,
          marginBottom: 10,
        }}
      >
        <CircleCheckIcon
          checked={patient?.oacContraindicated}
        />
        <span style={{ fontWeight: 600 }}>OAC Contraindicated</span>
        <Tooltip title="Indicates oral anticoagulation is contraindicated for this patient.">
          <InfoCircleOutlined className={classes.alertInfo} />
        </Tooltip>
      </div>
      <div className="icd-codes">
        <Descriptions size="large">
          <Descriptions.Item label="ICD Codes">
            <List
              size="small"
              dataSource={
                patient.deviceIcdCode
                  ? patient.icdCodes.concat([patient.deviceIcdCode])
                  : patient.icdCodes
              }
              renderItem={code => (
                <List.Item>{`${code.code} - ${code.description}`}</List.Item>
              )}
            />
          </Descriptions.Item>
        </Descriptions>
      </div>
      <Divider orientation="left">Notification Preferences</Divider>
      <Descriptions column={3} size="small">
        <Descriptions.Item label="Remind transmissions">
          {remindTransmissions ? 'Yes' : 'No'}
        </Descriptions.Item>
        <Descriptions.Item label="Notify data reception">
          {notifyDataReception ? 'Yes' : 'No'}
        </Descriptions.Item>
        <Descriptions.Item>
          <Button onClick={showPreferencesModal} type="primary">
            Change
          </Button>
          <Modal
            title="Preferences"
            open={preferencesModalVisible}
            onCancel={cancelPreferences}
            onOk={savePreferences}
            cancelText={null}
            okType="primary"
          >
            <List title="Notifications">
              <List.Item>
                <Checkbox
                  checked={remindTransmissions}
                  onChange={handleReminderChange}
                >
                  Remind missed transmissions
                </Checkbox>
              </List.Item>
              <List.Item>
                <Checkbox
                  checked={notifyDataReception}
                  onChange={handleDataReceptionChange}
                >
                  Notify data reception
                </Checkbox>
              </List.Item>
            </List>
          </Modal>
        </Descriptions.Item>
      </Descriptions>
      <Divider orientation="left">
        Alerts, Remote Reports, and Office Checks
      </Divider>
      {id && practice?.id ? (
        <Timeline patientId={id} practiceId={practice.id} />
      ) : (
        <Empty />
      )}

      <Divider orientation="left">Transmissions</Divider>

      <List
        title="Transmissions"
        dataSource={transmissionsList}
        renderItem={item => (
          <List.Item>
            <List.Item.Meta
              title={
                <>
                  <span className={classes.trDate}>
                    <span className={classes.miniBlock} />
                    {item.transmissionDate &&
                    dayjs(item.transmissionDate).isValid()
                      ? dayjs(item.transmissionDate).format('MMM Do YYYY')
                      : 'Manually Uploaded'}
                  </span>
                  <span className={classes.trDevice}>
                    <Tag color="blue">{item.device.model}</Tag>
                  </span>
                  <span className={classes.trView}>
                    <Tooltip title="Open in a new tab" placement="top">
                      <Link to={`/transmissions/${item.id}`} target="_blank">
                        <Button type="link" icon={<ExportOutlined />} />
                      </Link>
                    </Tooltip>
                  </span>
                </>
              }
            />
          </List.Item>
        )}
      />
      <div>
        <Text type="strong">
          Showing the latest {transmissionsList?.length || 0} of{' '}
          {transmissionsCount} transmissions
        </Text>
        <span className={classes.mlMedium}>
          {(transmissionsList?.length || 0) < transmissionsCount && (
            <Button type="link" onClick={showTransmissions}>
              View all transmissions
            </Button>
          )}
        </span>
      </div>

      <Modal
        title="Removal options"
        open={removalOptionsVisible}
        onCancel={hideRemovalOptions}
        cancelText={null}
        okType="secondary"
        footer={null}
      >
        <List title="Deactivate">
          <List.Item>
            <div>
              <strong>Deactivate this patient</strong>
              <p>
                Won&apos;t be visible by default in the patient list but the
                information will be available.
              </p>
            </div>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              className={`delete-button ${classes.dangerZoneButton}`}
              onClick={handleDeactivate}
              loading={deactivating}
            >
              Deactivate
            </Button>
          </List.Item>
          <List.Item>
            <div>
              <strong>Full remove this patient</strong>
              <p>
                The patient will be deleted from the database. This action
                cannot be reverted.
              </p>
            </div>
            <Button
              danger
              icon={<DeleteOutlined />}
              className={`hard-delete-button ${classes.dangerZoneButton}`}
              onClick={showFullRemoval}
            >
              Full removal
            </Button>
          </List.Item>
        </List>
        <div className={classes.modalButtons}>
          <Divider />
          <Button type="secondary" onClick={hideRemovalOptions}>
            Close
          </Button>
        </div>
      </Modal>
      <Modal
        title="Patient reminders"
        open={remindersOptionsVisible}
        onCancel={hideRemindersOptions}
        cancelText={null}
        okType="secondary"
        footer={null}
      >
        <List title="Reminders">
          <List.Item>
            <div>
              <strong>Make a reminder phone call</strong>
              <p>
                A reminder call will be made to the patient&apos;s phone number.
              </p>
            </div>
            <Button
              type="primary"
              ghost
              icon={<PhoneOutlined />}
              className={`delete-button ${classes.dangerZoneButton}`}
              onClick={makeReminderCall}
              loading={makingReminderCall}
            >
              Make reminder call
            </Button>
          </List.Item>
          <List.Item>
            <div>
              <strong>Send an SMS reminder</strong>
              <p>
                An SMS message will be sent to the patient&apos;s mobile phone
                number.
              </p>
            </div>
            <Button
              type="primary"
              ghost
              icon={<MessageOutlined />}
              className={`hard-delete-button ${classes.dangerZoneButton}`}
              onClick={sendReminderSms}
              loading={sendingReminderSms}
            >
              Send reminder SMS
            </Button>
          </List.Item>
        </List>
        <div className={classes.modalButtons}>
          <Divider />
          <Button type="secondary" onClick={hideRemindersOptions}>
            Close
          </Button>
        </div>
      </Modal>
      <FullRemovalModal
        patientId={id}
        onCancel={hideFullRemoval}
        visible={fullRemovalVisible}
      />
      <CommentsTables
        collectionName="patients"
        id={patient.id}
        containerClassName={classes.comments}
        practiceId={patient.practiceId}
      />

      <TransmissionsModal
        patientId={patient.id}
        visible={showTransmissionsModal}
        onCancel={() => setShowTransmissionsModal(false)}
      />

      <style>
        {`
          .icd-codes .ant-descriptions-item-content {
            display: block;
          }
        `}
      </style>
    </div>
  )
}
